import { named, withDependencies } from '@wix/thunderbolt-ioc'
import {
	ILayoutDoneService,
	LayoutDoneServiceSymbol,
	EditorFeatureConfigSymbol,
	LOADING_PHASES,
	LoggerSymbol,
	ILogger,
	ViewerManagerStructureAPISymbol,
} from '@wix/thunderbolt-symbols'
import {
	Component,
	ComponentMeasureMap,
	SiteMeasureMap,
	ViewerAPI,
	Callback,
	LayoutCallback,
	LayoutDoneCbArgs,
} from 'thunderbolt-viewer-manager-types'
import { MeasureMapApiSymbol, name } from '../symbols'
import type { ViewerManagerLayoutApiEditorConfig, MeasureMapApi, LayoutRegistrar } from '../types'
import { VariantsApiSymbol, ViewerManagerVariantsApiExtension } from 'ds-feature-viewer-manager-variants-api'
import { get, pick } from 'lodash'

type LayoutedComponentsData = {
	structure: Record<string, Component>
	layoutedCompsMeasureMap: Record<string, ComponentMeasureMap>
}

const getMeasuresFromMap = (measureMap: SiteMeasureMap, compId: string): ComponentMeasureMap => {
	return {
		width: measureMap.width[compId],
		height: measureMap.height[compId],
		relativeToContainerTop: measureMap.top[compId],
		relativeToContainerLeft: measureMap.left[compId],
	}
}

const getMeasuresFromStructure = (compStructure: Component): ComponentMeasureMap => {
	return {
		width: compStructure.layout.width!,
		height: compStructure.layout.height!,
		relativeToContainerTop: compStructure.layout.y,
		relativeToContainerLeft: compStructure.layout.x,
	}
}

// DM registers to layout done twice and there is no need to measure the same components that were dispatched from the same change twice
const layoutDoneCache: Map<string, LayoutDoneCbArgs> = new Map()

const clearLayoutDoneCacheExceptCurrentDispatch = (dispatchId: string) => {
	const args = layoutDoneCache.get(dispatchId)
	layoutDoneCache.clear()

	if (args) {
		layoutDoneCache.set(dispatchId, args)
	}
}

export const RegisterLayoutApi = withDependencies(
	[
		named(EditorFeatureConfigSymbol, name),
		ViewerManagerStructureAPISymbol,
		LayoutDoneServiceSymbol,
		LoggerSymbol,
		MeasureMapApiSymbol,
		VariantsApiSymbol,
	],
	(
		config: ViewerManagerLayoutApiEditorConfig,
		structureAPI: ViewerAPI['structure'],
		layoutDoneService: ILayoutDoneService,
		logger: ILogger,
		measureMapApi: MeasureMapApi,
		{ isDuringInteractionMode }: ViewerManagerVariantsApiExtension
	): LayoutRegistrar => {
		const measureMap = measureMapApi.getSiteMeasureMap()
		let isFirstRegisterToNextRenderDone = true

		const checkMeasuresAreValidAndReport = ({ layoutedCompsMeasureMap, structure }: LayoutedComponentsData) => {
			const largeNumberForMeasures = 1000000 // Arbitrary large number for height/width
			Object.entries(layoutedCompsMeasureMap).forEach(([compId, layoutedCompMeasureMap]) => {
				if (
					layoutedCompMeasureMap.height > largeNumberForMeasures ||
					layoutedCompMeasureMap.height < -largeNumberForMeasures ||
					layoutedCompMeasureMap.width > largeNumberForMeasures ||
					layoutedCompMeasureMap.width < -largeNumberForMeasures
				) {
					logger.meter('invalid_measures_for_comp', {
						customParams: { compId, measures: layoutedCompMeasureMap },
					})
				}

				// TODO: DELETE LATER
				// try to analyse a reason for a HUGE STRIP bug: Jira: TB-6189
				// note: 50000 is the magic number, limit in DS for component height
				if (layoutedCompMeasureMap.height > 50001) {
					const structureHeight = get(structure[compId], ['layout', 'height'])

					// report only when it happens on the first time
					if (structureHeight && structureHeight < 50000) {
						logger.meter('Measured HUGE height for component on PostUpdateOperation', {
							paramsOverrides: {
								extra: JSON.stringify({
									measuredLayout: layoutedCompMeasureMap,
									structure: pick(structure[compId], ['id', 'componentType', 'layout']),
								}),
							},
						})
					}
				}
			})
		}

		const getLayoutedCompsData = (compIds: Array<string>): LayoutedComponentsData => {
			const isDeadComp = (compId: string) => {
				const element = document.getElementById(compId)
				return !element || element.getAttribute('data-dead-comp') === 'true'
			}
			const shouldTakeLayoutFromStructure = isDuringInteractionMode()
			return compIds.reduce(
				(acc, compId) => {
					const compStructure = structureAPI.getStructure(compId)
					if (compStructure && !isDeadComp(compId)) {
						acc.structure[compId] = compStructure
						acc.layoutedCompsMeasureMap[compId] = shouldTakeLayoutFromStructure
							? getMeasuresFromStructure(acc.structure[compId])
							: getMeasuresFromMap(measureMap, compId)
					}

					return acc
				},
				{ structure: {}, layoutedCompsMeasureMap: {} } as LayoutedComponentsData
			)
		}

		const handleComponentsLayoutChange = (
			handler: LayoutCallback,
			changedCompIds: Array<string>,
			dispatchId: string
		) => {
			clearLayoutDoneCacheExceptCurrentDispatch(dispatchId)
			if (layoutDoneCache.has(dispatchId)) {
				handler(layoutDoneCache.get(dispatchId)!)
				return
			}

			const compsData = getLayoutedCompsData(changedCompIds)

			const layoutDoneCbArgs = {
				isMobile: config.isMobileView,
				layoutedCompsMeasureMap: compsData.layoutedCompsMeasureMap,
				structure: compsData.structure,
			}

			layoutDoneCache.set(dispatchId, layoutDoneCbArgs)
			handler(layoutDoneCbArgs)
			checkMeasuresAreValidAndReport(compsData)
		}

		const handleNextRenderDone = (handler: Callback) => {
			logger.phaseStarted(LOADING_PHASES.DS_WAIT_FOR_LAYOUT_DONE)
			if (isFirstRegisterToNextRenderDone) {
				handler()
				isFirstRegisterToNextRenderDone = false
				logger.phaseEnded(LOADING_PHASES.DS_WAIT_FOR_LAYOUT_DONE)
				return
			}
			layoutDoneService.registerToRenderDone(
				() => {
					logger.phaseEnded(LOADING_PHASES.DS_WAIT_FOR_LAYOUT_DONE)
					handler()
				},
				{ once: true },
				true
			)
		}

		return {
			registerToNextRenderDone: (handler) => {
				handleNextRenderDone(handler)
			},
			registerToRenderDone: (handler) => {
				layoutDoneService.registerToRenderDone(
					() => {
						// @ts-ignore: remove 'ignore' after ViewerApi in dm updated
						handler()
					},
					undefined,
					true
				)
			},
			registerToComponentsLayoutChange: (handler) => {
				layoutDoneService.registerToComponentsLayoutChange(
					(changedCompIds, dispatchId) => {
						if (changedCompIds.length === 0) {
							return
						}
						handleComponentsLayoutChange(handler, changedCompIds, dispatchId)
					},
					undefined,
					true
				)
			},
		}
	}
)
